import { defaultTheme } from 'react-admin';

const brandPrimary = '#E31837';
const brandSecondary = '#333333';
const brandBackground = '#F5F5F5';

export const customTheme = {
    ...defaultTheme,
    palette: {
        primary: {
            main: brandPrimary,
            light: '#FF1F3F',
            dark: '#C41430',
            contrastText: '#ffffff',
        },
        secondary: {
            main: brandSecondary,
            light: '#4A4A4A',
            dark: '#1A1A1A',
            contrastText: '#ffffff',
        },
        background: {
            default: brandBackground,
            paper: '#ffffff',
        },
        text: {
            primary: '#333333',
            secondary: '#666666',
        },
        error: {
            main: '#E31837',
        }
    },
    shape: {
        borderRadius: 4,
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontWeight: 600,
            fontSize: '2.5rem',
        },
        h2: {
            fontWeight: 600,
            fontSize: '2rem',
        },
        h3: {
            fontWeight: 600,
            fontSize: '1.75rem',
        },
        h4: {
            fontWeight: 500,
            fontSize: '1.5rem',
        },
        h5: {
            fontWeight: 500,
            fontSize: '1.25rem',
        },
        h6: {
            fontWeight: 500,
            fontSize: '1rem',
        },
        body1: {
            fontSize: '0.95rem',
        },
        body2: {
            fontSize: '0.875rem',
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    textTransform: 'none',
                    fontWeight: 500,
                    padding: '8px 24px',
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                        backgroundColor: '#FF1F3F',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    boxShadow: 'none',
                    border: 'none',
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiPaper: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    backgroundImage: 'none',
                    border: 'none',
                    '& .MuiTabs-root': {
                        borderTop: 'none !important',
                        '&::before': {
                            display: 'none',
                        },
                        '&::after': {
                            display: 'none',
                        },
                    },
                    '&[class*="RaSelectColumnsMenu-paper"], &.MuiPopover-paper': {
                        backgroundColor: '#fff !important',
                        boxShadow: '0px 4px 16px rgba(0,0,0,0.12)',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                    },
                },
            },
        },
        RaShow: {
            styleOverrides: {
                root: {
                    '& .RaShow-main': {
                        marginTop: 0,
                        backgroundColor: '#fff',
                        border: 'none',
                        '& hr': {
                            display: 'none',
                        },
                        '& .message-content, & .email-content, & .ra-field-body, & .ra-field-content, & .ra-field-text': {
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'inherit',
                            lineHeight: 1.6,
                            maxWidth: '100%',
                            padding: '16px 0',
                        },
                    },
                    '& .MuiCard-root': {
                        padding: '24px 32px',
                    },
                    '& .RaShow-noActions': {
                        marginTop: 0,
                    },
                    '& > div > div': {
                        boxShadow: 'none',
                        border: 'none',
                        backgroundColor: '#fff',
                        '& hr': {
                            display: 'none',
                        },
                    },
                },
            },
        },
        RaTabbedShowLayout: {
            styleOverrides: {
                root: {
                    backgroundColor: '#fff',
                    border: 'none',
                    '&::before': {
                        display: 'none',
                    },
                    '&::after': {
                        display: 'none',
                    },
                    '& .MuiTabs-root': {
                        marginBottom: 24,
                        borderBottom: '1px solid #e0e0e0',
                        borderTop: 'none',
                        backgroundColor: '#fff',
                        '&::before': {
                            display: 'none',
                        },
                        '&::after': {
                            display: 'none',
                        },
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            minWidth: 100,
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            color: '#666666',
                            '&.Mui-selected': {
                                color: brandPrimary,
                            },
                        },
                    },
                    '& .MuiTabPanel-root': {
                        padding: '24px 0',
                        backgroundColor: '#fff',
                        border: 'none',
                        '&::before': {
                            display: 'none',
                        },
                        '&::after': {
                            display: 'none',
                        },
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    '& .MuiTabs-indicator': {
                        backgroundColor: brandPrimary,
                    },
                },
            },
        },
        RaLabeled: {
            styleOverrides: {
                label: {
                    fontSize: '0.875rem',
                    color: '#666666',
                    marginBottom: 8,
                },
                value: {
                    fontSize: '0.95rem',
                    marginBottom: 24,
                },
            },
        },
        RaMenuItemLink: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    margin: '4px 8px',
                    padding: '8px 12px',
                    fontWeight: 500,
                    '&.RaMenuItemLink-active': {
                        backgroundColor: 'rgba(227, 24, 55, 0.15)',
                        color: '#E31837',
                        fontWeight: 600,
                        borderLeft: '3px solid #E31837',
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                },
            },
        },
        RaDatagrid: {
            styleOverrides: {
                root: {
                    '& .RaDatagrid-headerCell': {
                        backgroundColor: brandPrimary,
                        height: '48px',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#ffffff',
                        '& .MuiButtonBase-root': {
                            color: '#ffffff',
                            fontWeight: 600,
                        },
                        '& .MuiTableSortLabel-icon': {
                            color: '#ffffff !important',
                            opacity: 0.7,
                        },
                        '& .MuiTableSortLabel-root:hover .MuiTableSortLabel-icon': {
                            opacity: 1,
                        },
                        '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': {
                            color: '#ffffff !important',
                            opacity: 1,
                        },
                    },
                    '& .RaDatagrid-row': {
                        height: '52px',
                        '&:hover': {
                            backgroundColor: 'rgba(227, 24, 55, 0.04)',
                        },
                    },
                    '& .RaDatagrid-rowCell': {
                        fontSize: '0.95rem',
                        padding: '12px 16px',
                    },
                    '& .column-select': {
                        paddingLeft: '16px',
                    },
                },
            },
        },
        RaList: {
            styleOverrides: {
                root: {
                    '& .MuiPaper-root': {
                        padding: '16px 24px',
                        border: '1px solid #e0e0e0',
                        backgroundColor: '#fff',
                    },
                    '& .MuiToolbar-root': {
                        minHeight: '48px',
                        paddingLeft: 0,
                        paddingRight: 0,
                        marginBottom: '16px',
                    },
                    '& .MuiTablePagination-root': {
                        marginTop: '16px',
                    },
                },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    '& .MuiTablePagination-select': {
                        fontSize: '0.95rem',
                    },
                    '& .MuiTablePagination-displayedRows': {
                        fontSize: '0.95rem',
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    padding: '6px',
                },
            },
        },
        RaLogin: {
            styleOverrides: {
                root: {
                    backgroundImage: 'linear-gradient(135deg, #1a237e 0%, #121858 100%)',
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    paddingTop: '15vh',
                    '& .MuiCard-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        margin: 0,
                        padding: 0,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '-10vh',
                    },
                    '& .MuiAvatar-root': {
                        display: 'none',
                    },
                },
            },
        },
        RaLoginForm: {
            styleOverrides: {
                root: {
                    width: '100%',
                    '& .MuiCardContent-root': {
                        padding: 0,
                    },
                    '& .MuiTextField-root': {
                        marginBottom: '20px',
                        width: '100%',
                    },
                    '& .MuiButton-root': {
                        width: '100%',
                        marginTop: '24px',
                        padding: '12px',
                    },
                    '& .notification': {
                        marginTop: '16px',
                    },
                },
            },
        },
        RaFilterForm: {
            styleOverrides: {
                root: {
                    minHeight: 'unset',
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#fff',
                    padding: '24px',
                    borderRadius: '8px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                },
            },
        },
    },
}; 